import React, { useState } from 'react';
import Checkbox, { CheckboxChangeEvent } from 'antd/es/checkbox';
import { formatCurrency } from '@shared/utils/currency';
import {
	CANALS,
	Currencies,
	LinerCostUnitDefaultLabels,
	LinerCostUnitDefaults,
	VesselTypeLabels,
	VesselTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type OrgCanalTransitDefaults from '@api/models/org-canal-transit-defaults';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import showErrorNotification from '@client/utils/showErrorNotification';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	createUserCanalDefault,
	deleteUserCanalDefault,
	getUserData,
	updateOrganizationDetails,
	updateUserCanalDefault,
} from '@client/lib/api';
import EditableTable from '@client/components/EditableTable';
import getSizeOptions from '@client/screens/fleet/VesselDetailsScreen/helpers/getVesselSizeOptions';
import styles from './EstimatorSettings.module.css';

const DefaultEstimatorSettings = () => {
	const [userData, refreshUserData] = useFetchedState(getUserData);
	const [editingValues, setEditingValues] = useState<Partial<OrgCanalTransitDefaults> | null>(null);

	const columns = [
		{
			dataIndex: 'canalTransitName',
			title: 'Canal',
			editable: true,
			editingProps: {
				type: 'select',
				options: CANALS
					.map((canal) => ({
						key: canal.id,
						value: canal.id,
						label: canal.name,
					})),
				inputProps: {
					showSearch: true,
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							canalTransitName: CANALS.find((c) => c.id === v as number)!.name as string,
							canalTransitId: v as number,
						}));
					},
				},
			},
			width: 200,
		},
		{
			dataIndex: 'idleTime',
			title: 'Idle Time',
			editable: true,
			editingProps: {
				type: 'number',
				inputProps: {
					addonAfter: 'days',
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							idleTime: v,
						}));
					},
				},
			},
			render: (v: number | null) => v == null ? '—' : `${v} days`,
			width: 170,
		},
		{
			dataIndex: 'cost',
			title: 'Cost',
			editable: true,
			render: (
				c: number | null,
				row: any,
			) => formatCurrency(c ?? 0 as number, row.currency ?? Currencies.USD),
			editingProps: {
				type: 'currency',
				inputProps: {
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							cost: v as number,
						}));
					},
				},
			},
			width: 200,
		},
		{
			dataIndex: 'vesselType',
			title: 'Vessel Type',
			editable: !editingValues?.applyToAllVessels,
			editingProps: {
				type: 'select',
				options: (Object.keys(VesselTypes) as Array<keyof typeof VesselTypes>).map((t) => ({
					label: VesselTypeLabels[t],
					value: t,
				})),
				inputProps: {
					showSearch: true,
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							vesselType: v as Values<typeof VesselTypes>,
							vesselSize: '',
						}));
					},
				},
			},
			width: 230,
			render: (t: Values<typeof VesselTypes>) => VesselTypeLabels[t],
		},
		{
			dataIndex: 'vesselSize',
			title: 'Vessel Size',
			editable: !editingValues?.applyToAllVessels,
			editingProps: {
				type: 'select',
				placeholder: 'Vessel Size',
				options: getSizeOptions(editingValues?.vesselType as Values<typeof VesselTypes>),
				inputProps: {
					value: editingValues?.vesselSize as any,
					showSearch: true,
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							vesselSize: v as string,
						}));
					},
				},
			},
			width: 200,
			render: (c: string) => c ?? '',
		},
		{
			dataIndex: 'applyToAllVessels',
			title: 'Apply To All Vessels',
			editable: true,
			align: 'center',
			editingProps: {
				type: 'check',
				inputProps: {
					checked: editingValues?.applyToAllVessels,
					onChange: (v: CheckboxChangeEvent) => {
						setEditingValues((prev) => ({
							...prev,
							applyToAllVessels: v.target.checked,
						}));
					},
				},
			},
			width: 200,
			render: (c: boolean) => (<Checkbox disabled checked={c} />),
		},
	];

	const handleAddNewCanalUserDefault = async () => {
		if (!editingValues?.canalTransitId) {
			showErrorNotification('Canal is required');

			return;
		}

		const {
			canalTransitName,
			canalTransitId,
			cost = 0,
			idleTime = 0,
			vesselSize = '',
			vesselType = '',
			applyToAllVessels = false,
		} = editingValues;

		if (!canalTransitName || !userData?.id) {
			showErrorNotification('Missing required data');

			return;
		}

		await createUserCanalDefault({
			canalTransitName,
			canalTransitId,
			cost: Number(cost ?? 0),
			idleTime: Number(idleTime ?? 0),
			vesselSize: applyToAllVessels ? '' : (vesselSize ?? ''),
			vesselType: applyToAllVessels ? '' : (vesselType ?? ''),
			applyToAllVessels,
		});

		await refreshUserData();
		setEditingValues(null);
	};

	const handleUpdateCanalUserDefault = async (id: number, row: OrgCanalTransitDefaults) => {
		const finalValues = {
			...row,
			...editingValues,
		};

		if (finalValues.canalTransitId == null) {
			throw showErrorNotification('Canal is required');
		}

		const {
			canalTransitName,
			canalTransitId,
			cost = 0,
			idleTime = 0,
			vesselSize = '',
			vesselType = '',
			applyToAllVessels = false,
		} = finalValues;

		await updateUserCanalDefault({
			id,
			canalTransitName,
			canalTransitId,
			cost: Number(cost ?? 0),
			idleTime: Number(idleTime ?? 0),
			vesselSize: applyToAllVessels ? '' : (vesselSize ?? ''),
			vesselType: applyToAllVessels ? '' : (vesselType ?? ''),
			applyToAllVessels,
		});

		await refreshUserData();
		setEditingValues(null);
	};

	const handleDeleteCanalUserDefault = async (id: number) => {
		await deleteUserCanalDefault({ id });
		await refreshUserData();
	};

	return (
		<>
			<EditableDetails
				className={styles.details}
				labelWidth={300}
				items={[
					{
						key: 'defaultSeaMargin',
						label: 'Default sea margin %',
						editable: true,
						type: 'percentage',
						value: userData?.defaultSeaMargin ?? 5,
					},
					{
						key: 'defaultEstimatorLinerCostUnit',
						label: 'Liner cost units',
						editable: true,
						type: 'select',
						options: Object.entries(LinerCostUnitDefaultLabels)
							.map(([value, label]) => ({
								value,
								label,
							})),
						value: 
                            userData?.defaultEstimatorLinerCostUnit ?? 
                            LinerCostUnitDefaults.PER_CARGO_UNIT,
					},
				]}
				onSave={async (values) => {
					try {
						await updateOrganizationDetails(values);
						await refreshUserData();
					} catch (_e) {
						showErrorNotification('Cannot update report group');
					}
				}}
			/>
			<EditableTable<OrgCanalTransitDefaults, 'id'>
				keyDataIndex="id"
				rowKey="id"
				key={userData?.orgCanalTransitDefaults?.length}
				columns={columns as any}
				dataSource={
					userData?.orgCanalTransitDefaults
						.slice()
						.sort((a, b) => {
							const nameCompare = a.canalTransitName.localeCompare(b.canalTransitName);

							if (nameCompare !== 0) {
								return nameCompare;
							}

							return a.id - b.id;
						}) ?? []
				}
				editingRow={editingValues?.id ?? null}
				enableEdit
				onEditingRowChange={(_, row) => {
					setEditingValues(row ?? null);
				}}
				onSave={handleUpdateCanalUserDefault}
				onAddNew={handleAddNewCanalUserDefault}
				deleteMessage="Are you sure you want to remove this entry"
				onDelete={handleDeleteCanalUserDefault}
				allowAddNew
				addNewText="Canal Default Time & Costs"
				enableDelete
			/>
		</>
	);
};

export default DefaultEstimatorSettings;